// Wohler Chemical — Opioid/Harm-Reduction + Iboga Analogs (Programs A/D/H)

export const opioidIbogaCompounds = {
  "corynoxine-b": {
    id: "corynoxine-b",
    name: "Corynoxine B",
    fullName: "Corynoxine B (and Corynoxine A)",
    aliases: [],
    categoryId: "opioid",
    program: "Program A / J",
    formula: "C₂₂H₂₈N₂O₄",
    molecularWeight: "384.47 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 70,
    harmScore: 18,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — expands Wohler portfolio into neuroprotection",
    summary: "Oxindole alkaloid found in kratom and Uncaria species. Emerging neuroprotective profile with autophagy-promoting activity; USC Parkinson's research. Acts on calcium channels and dopaminergic pathways — distinct from opioid mechanism.",
    pros: ["Non-opioid mechanism — low diversion/scheduling risk", "Neuroprotection angle (Parkinson's, neurodegeneration)", "Natural-product narrative", "Low intrinsic abuse liability"],
    cons: ["Smaller therapeutic signal than opioid-active alkaloids", "Limited pharmacology literature"],
    pharmacology: {
      targets: [
        { receptor: "Calcium channels", activity: "Modulator", value: "Mechanism in neuroprotection" },
        { receptor: "Dopamine pathways", activity: "Indirect modulator", value: "Under study" }
      ],
      duration: "Unknown for humans",
      mechanism: "Autophagy induction, neuroprotection — distinct from opioid pathway"
    },
    labSynthesis: {
      title: "Laboratory-Scale Isolation",
      overview: "Isolation from kratom or Uncaria rhynchophylla biomass. Distinct Rf from mitragynine on silica TLC.",
      steps: [
        { step: "1", title: "Total alkaloid extraction", description: "Acid/base extraction analogous to mitragynine.", solvent: "Aqueous acid → EtOAc", keyReagents: "Acetic acid, Na₂CO₃" },
        { step: "2", title: "Chromatography", description: "Silica EtOAc/MeOH gradient.", solvent: "EtOAc/MeOH", keyReagents: "Silica" },
        { step: "3", title: "Recrystallization", description: "From ethanol.", solvent: "Ethanol", keyReagents: "Ethanol" }
      ],
      yield: "~0.3–0.8 g per kg dry leaf (corynoxine-rich source)",
      purityTarget: "≥ 98 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Low — biomass-limited. 50–100 g realistic first scale.",
      solventsUsed: ["Ethyl Acetate", "Ethanol", "Methanol", "Heptane"],
      equipment: ["Standard extraction", "SMB chromatography for economy"],
      batchPlan: { targetBatch: "100 g", startingMaterial: "~150 kg Uncaria biomass", keyVolumes: ["EtOAc: 300 L", "EtOH: 50 L"] },
      hazards: ["Standard extraction hazards"],
      vendorNotes: ["Uncaria biomass — specialty herb suppliers"],
      estCostPerKg: "$45,000+ at 100-g scale"
    },
    safety: {
      acuteToxicity: "Favorable — natural product with traditional use",
      chronicConcerns: ["Limited long-term data"],
      contraindications: ["Unknown drug interactions"],
      acuteSideEffects: ["Generally mild"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Corynoxine and derivatives in pain management. PMC. 2025." },
      { citation: "USC Keck School of Medicine. Corynoxine B autophagy research in Parkinson's models." }
    ],
    patentInfo: "Natural product; method/formulation patents available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 14, unmetMedicalNeed: 18, novelty: 12, universityInterest: 8 },
      harmBreakdown: { acuteToxicity: 4, chronicToxicity: 6, dependence: 2, schedulingRisk: 6 },
      netValue: 52,
      confidenceRationale: "MODERATE — intriguing neuroprotection angle; data is emerging."
    },
    priorityTier: "Tier 2",
    nextSteps: ["Collaborate with neurodegeneration lab at USC or equivalent", "100-g reference-standard production"]
  },

  "sr-17018": {
    id: "sr-17018",
    name: "SR-17018",
    fullName: "SR-17018 (biased MOR agonist)",
    aliases: [],
    categoryId: "opioid",
    program: "Program D",
    formula: "C₂₀H₁₉F₃N₄O (approximate)",
    molecularWeight: "≈ 404 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled but structurally in the biased-opioid research class. Very high analog/enforcement/diversion concern despite research-only posture. Brorphine-like structural class has drawn scrutiny.",
    benefitScore: 75,
    harmScore: 55,
    evidenceConfidence: "MODERATE",
    status: "Hold — Legal Review Required",
    statusColor: "red",
    goDecision: "HOLD — require legal memo + university/CRO partner before any synthesis",
    summary: "A biased mu-opioid receptor agonist that preferentially activates G-protein signaling over β-arrestin recruitment. The biased-opioid hypothesis posits reduced tolerance and respiratory depression at equianalgesic doses. The hypothesis is debated — recent replication work has produced mixed results. Relevant to fentanyl-harm-reduction research but only in a tightly controlled institutional context.",
    pros: [
      "Most characterized biased MOR agonist in research literature",
      "Directly relevant to fentanyl-harm-reduction + tolerance/dependence research",
      "Clear mechanism hypothesis for rigorous testing"
    ],
    cons: [
      "Brorphine-like structural class — enforcement concern",
      "Biased-opioid hypothesis not yet fully validated",
      "Potent MOR agonism = abuse/diversion risk",
      "Controversial compound — reputational consideration for Wohler"
    ],
    pharmacology: {
      targets: [
        { receptor: "μ-opioid", activity: "Biased agonist (G-protein > β-arrestin)", value: "Low-nM affinity; functionally biased signaling" }
      ],
      duration: "Pending characterization",
      mechanism: "Biased MOR agonism — theoretically reduces β-arrestin-driven side effects (respiratory depression, tolerance)"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Multi-step synthesis from piperidinyl benzimidazole scaffold precursors. Exact procedure is published in Schmid et al. 2017.",
      steps: [
        { step: "1", title: "Piperidine scaffold construction", description: "From commercial piperidine + benzimidazole coupling.", solvent: "DMF (substitute with acetonitrile for Wohler)", keyReagents: "Piperidine, benzimidazole" },
        { step: "2", title: "Fluorinated aryl coupling", description: "Suzuki or SNAr installation of fluorinated aryl.", solvent: "Dioxane/EtOAc", keyReagents: "Pd catalyst" },
        { step: "3", title: "Final HCl salt", description: "HCl in EtOAc or ethanol.", solvent: "EtOAc/EtOH", keyReagents: "HCl" }
      ],
      yield: "10–25 % over multi-step route",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Not Recommended for Kg Scale",
      feasibility: "Very Low — Wohler should not commit kg-scale capacity to this compound without extensive legal review and institutional partner. 1–10 g is a defensible research tool scale.",
      solventsUsed: ["Acetonitrile", "Ethyl Acetate", "THF", "Ethanol"],
      equipment: ["Standard multi-step synthesis"],
      batchPlan: { targetBatch: "1–5 g (reference research tool scale)", startingMaterial: "Published precursors", keyVolumes: ["Scale: research only"] },
      hazards: ["Potent opioid — highest industrial hygiene tier; Pd catalysts; multi-step contamination control"],
      vendorNotes: ["Palladium catalysts: Strem, Sigma"],
      estCostPerKg: "Not a production compound — research-scale only"
    },
    safety: {
      acuteToxicity: "Preclinical data only; assume high-potency opioid toxicity class",
      chronicConcerns: ["Dependence", "Respiratory depression (reduced in bias model but not absent)", "Tolerance"],
      contraindications: ["Same as high-potency opioid class"],
      acuteSideEffects: ["Potent analgesia", "Respiratory depression at higher dose"],
      ehsClassification: "Highest tier — containment cleanroom, OEL < 1 μg/m³"
    },
    publishedResearch: [
      { citation: "Schmid CL et al. Bias Factor and Therapeutic Window Correlate to Predict Safer Opioid Analgesics. Cell. 2017;171(5):1165-1175.e13." },
      { citation: "Kliewer A et al. Morphine-induced respiratory depression is independent of β-arrestin2 signalling. Br J Pharmacol. 2020;177(13):2923-2931." }
    ],
    patentInfo: "Scripps Research Institute holds key patents. Wohler would be licensee, not IP owner.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 20, evidenceQuality: 15, unmetMedicalNeed: 20, novelty: 14, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 15, chronicToxicity: 13, dependence: 13, schedulingRisk: 14 },
      netValue: 20,
      confidenceRationale: "MODERATE — good hypothesis; mixed replication; very high regulatory/reputational overhead."
    },
    priorityTier: "Tier 3 — Hold",
    nextSteps: ["Legal memo on scheduling/analog/enforcement risk", "Survey universities for interest before committing synthesis", "Consider KOR/DOR/NOP alternatives with lower risk profile first"]
  },

  "tabernanthalog": {
    id: "tabernanthalog",
    name: "Tabernanthalog (TBG)",
    fullName: "Tabernanthalog",
    aliases: ["TBG"],
    categoryId: "iboga",
    program: "Program H",
    formula: "C₁₉H₂₄N₂O",
    molecularWeight: "296.41 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Developed at UC Davis by the Olson lab; published Nature 2021. Active academic investigation.",
    benefitScore: 88,
    harmScore: 18,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — lead iboga-scaffold research tool",
    summary: "Non-hallucinogenic ibogaine-inspired analog. Promotes structural and functional neural plasticity in prefrontal cortex similar to ibogaine but without 5-HT2A hallucinogenic activity and without ibogaine's cardiac sodium channel blockade. Strong preclinical addiction + depression data. MDPI IJMS 2025 review: 'scaffold for silent neuroplasticity.'",
    pros: [
      "Non-hallucinogenic — no specialized psychedelic clinical infrastructure needed",
      "No cardiac NaV blockade (vs ibogaine) — major safety advantage",
      "Strong preclinical data: alcohol, heroin-seeking, stress-induced circuit disruption",
      "UC Davis backing — active academic publication pipeline",
      "Unscheduled, novel composition — strong IP opportunity"
    ],
    cons: [
      "Multi-step synthesis from indole starting materials",
      "Limited human data — still preclinical",
      "Ibogaine-adjacent branding may attract regulator attention"
    ],
    pharmacology: {
      targets: [
        { receptor: "TrkB / BDNF pathway", activity: "Activator", value: "Neuroplasticity driver" },
        { receptor: "Nicotinic acetylcholine α7/α9α10", activity: "Inhibitor", value: "Documented in PMC11151864 (2024)" },
        { receptor: "5-HT2A", activity: "Minimal", value: "Non-hallucinogenic" }
      ],
      duration: "Pending clinical PK",
      metabolism: "Hepatic CYP; metabolites under characterization",
      mechanism: "Psychoplastogen — promotes spinogenesis and synaptogenesis in PFC via TrkB activation without 5-HT2A hallucinogenic signaling"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Multi-step synthesis from commercially available indole starting materials per published 2021 Nature synthesis route.",
      steps: [
        { step: "1", title: "Indole core functionalization", description: "Starting from 5-substituted indole, install ethyl chain.", solvent: "THF", keyReagents: "Alkylation reagents" },
        { step: "2", title: "Azatricyclic framework", description: "Construct the iboga-like azatricyclic ring via Pictet-Spengler or similar.", solvent: "DCM (substitute EtOAc)", keyReagents: "Aldehyde + acid" },
        { step: "3", title: "Final refunctionalization", description: "Install final N-methyl and methoxy groups.", solvent: "MeOH/EtOH", keyReagents: "Methylating agents" },
        { step: "4", title: "Fumarate or HCl salt", description: "Crystallize as salt.", solvent: "Ethanol", keyReagents: "Fumaric acid or HCl" }
      ],
      yield: "15–30 % over 5–7 steps",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — multi-step synthesis but published route is robust.",
      solventsUsed: ["THF", "Ethyl Acetate", "Methanol", "Ethanol", "Heptane"],
      equipment: ["Multi-stage reactor train", "Flash chromatography", "Crystallizer"],
      batchPlan: {
        targetBatch: "100 g first, then 1 kg after optimization",
        startingMaterial: "~300 g indole precursor",
        keyVolumes: ["THF: 15 L", "EtOAc: 20 L", "EtOH: 10 L"]
      },
      hazards: ["Standard multi-step synthesis hazards; no exceptional new hazards"],
      vendorNotes: ["Indole precursors: Combi-Blocks, Matrix, Sigma"],
      estCostPerKg: "$35,000 – $55,000 at 1-kg scale after optimization"
    },
    safety: {
      acuteToxicity: "Favorable in preclinical rodent models",
      chronicConcerns: ["Long-term human data absent", "Nicotinic receptor inhibition may have unexplored consequences"],
      contraindications: ["Pregnancy (no data)"],
      acuteSideEffects: ["Generally well-tolerated in rodents", "No dissociation, no hallucination in rodent models"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Cameron LP et al. An analog of psychedelics restores functional neural circuits disrupted by unpredictable stress. Nature. 2021.", pmc: "PMC7874389" },
      { citation: "Tabernanthalog as a scaffold for silent neuroplasticity. Int J Mol Sci. 2025." },
      { citation: "Tabernanthalog and ibogainalog inhibit α7 and α9α10 nicotinic acetylcholine receptors. PMC. 2024.", pmc: "PMC11151864" }
    ],
    patentInfo: "UC Davis + Delix Therapeutics hold key composition-of-matter patents (Delix licensed). Wohler can pursue formulation/crystal-form/method-of-use patents or license research-scale from Delix.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 23, evidenceQuality: 20, unmetMedicalNeed: 22, novelty: 18, universityInterest: 5 },
      harmBreakdown: { acuteToxicity: 4, chronicToxicity: 6, dependence: 2, schedulingRisk: 6 },
      netValue: 70,
      confidenceRationale: "HIGH — strong preclinical data, active academic program, favorable safety."
    },
    priorityTier: "Tier 1 — Lead program H candidate",
    nextSteps: ["License/negotiate with Delix Therapeutics for research-scale supply", "UC Davis collaboration", "Process development for economical synthesis", "Build university research partner list"]
  },

  "ibogainalog": {
    id: "ibogainalog",
    name: "Ibogainalog",
    fullName: "Ibogainalog",
    aliases: [],
    categoryId: "iboga",
    program: "Program H",
    formula: "~C₂₀H₂₆N₂O",
    molecularWeight: "~310 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Structurally closer to ibogaine than TBG.",
    benefitScore: 75,
    harmScore: 22,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — paired research tool with TBG",
    summary: "Related structural analog to TBG with distinct nicotinic receptor pharmacology. PMC11151864 (2024) showed ibogainalog inhibits α7 and α9α10 nicotinic receptors with higher potency than GABAA or calcium channels — directly relevant to nicotine addiction research.",
    pros: ["Nicotinic receptor selective inhibition — novel mechanism", "Pairs well with TBG for comparative SAR", "Unscheduled", "Natural-product-inspired scaffold"],
    cons: ["Lesser-studied than TBG", "Multi-step synthesis"],
    pharmacology: {
      targets: [
        { receptor: "Nicotinic α7", activity: "Inhibitor", value: "Potent" },
        { receptor: "Nicotinic α9α10", activity: "Inhibitor", value: "Potent" },
        { receptor: "TrkB/BDNF", activity: "Activator (hypothesized)", value: "Under study" }
      ],
      duration: "Unknown",
      mechanism: "Nicotinic receptor antagonism + possible psychoplastogen activity"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Related synthetic route to TBG with modified substitution pattern.",
      steps: [
        { step: "1", title: "Indole scaffold", description: "Indole + chain building.", solvent: "THF", keyReagents: "Alkylation" },
        { step: "2", title: "Azatricyclic closure", description: "Ring formation via Pictet-Spengler type reaction.", solvent: "EtOAc", keyReagents: "Aldehyde/acid" },
        { step: "3", title: "Salt formation", description: "HCl or fumarate.", solvent: "Ethanol", keyReagents: "HCl or fumaric acid" }
      ],
      yield: "15–25 %",
      purityTarget: "≥ 98 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — same infrastructure as TBG",
      solventsUsed: ["THF", "Ethyl Acetate", "Ethanol", "Heptane"],
      equipment: ["Same as TBG"],
      batchPlan: { targetBatch: "100 g first", startingMaterial: "~300 g indole precursor", keyVolumes: ["Similar to TBG"] },
      hazards: ["Standard synthesis hazards"],
      vendorNotes: ["Combi-Blocks, Matrix, Sigma"],
      estCostPerKg: "$40,000 – $60,000 at 1-kg"
    },
    safety: {
      acuteToxicity: "Favorable in preclinical",
      chronicConcerns: ["Long-term nicotinic inhibition effects unknown"],
      contraindications: ["Pregnancy"],
      acuteSideEffects: ["Mild"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Tabernanthalog and ibogainalog inhibit α7 and α9α10 nicotinic acetylcholine receptors. PMC. 2024.", pmc: "PMC11151864" }
    ],
    patentInfo: "Delix/UC Davis IP relevant.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 19, evidenceQuality: 16, unmetMedicalNeed: 20, novelty: 14, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 4, chronicToxicity: 8, dependence: 2, schedulingRisk: 8 },
      netValue: 53,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Produce as companion to TBG", "Nicotine-addiction model research proposal"]
  },

  "18-mc": {
    id: "18-mc",
    name: "18-MC",
    fullName: "18-Methoxycoronaridine",
    aliases: ["18-Methoxycoronaridine"],
    categoryId: "iboga",
    program: "Program H",
    formula: "C₂₂H₂₈N₂O₃",
    molecularWeight: "368.47 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Derivative of a voacanga alkaloid, distinct from ibogaine itself (Schedule I).",
    benefitScore: 78,
    harmScore: 28,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — strong anti-addiction mechanism",
    summary: "Ibogaine analog developed by Glick/Savage as an anti-addiction research compound. Antagonist at α3β4 nicotinic acetylcholine receptor with substantially reduced hallucinogenic and cardiac liabilities vs ibogaine. Has been studied in rodent models for opioid, alcohol, nicotine, and stimulant addictions.",
    pros: [
      "Specific α3β4 nAChR mechanism — clean pharmacology",
      "Substantially reduced cardiac + hallucinogenic liability vs ibogaine",
      "Efficacy across opioid, alcohol, nicotine, cocaine addiction models",
      "Published clinical-scale synthesis routes available"
    ],
    cons: ["Complex multi-step synthesis from voacanga alkaloid precursors", "Limited human clinical data", "IP controlled (Savant HWP / MindMed etc.)"],
    pharmacology: {
      targets: [
        { receptor: "α3β4 nicotinic acetylcholine", activity: "Antagonist", value: "Selective; mechanism of anti-addiction activity" },
        { receptor: "NMDA", activity: "Weak", value: "Minor contribution" }
      ],
      duration: "Shorter than ibogaine; 4–8 h estimated",
      mechanism: "α3β4 nAChR antagonism disrupts mesolimbic reward pathways without 5-HT2A hallucinogenic activity or cardiac NaV blockade"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Semi-synthesis from voacanga africana alkaloids (noribogaine, coronaridine precursors); O-methylation and side-chain installation.",
      steps: [
        { step: "1", title: "Voacanga alkaloid isolation", description: "Acid/base extraction from voacanga africana seeds.", solvent: "EtOAc/aqueous", keyReagents: "Voacanga africana seeds" },
        { step: "2", title: "Functional group modification", description: "Selective transformations to introduce 18-methoxy group.", solvent: "THF/MeOH", keyReagents: "Methylating agents" },
        { step: "3", title: "Final purification", description: "Chromatography + crystallization.", solvent: "EtOAc/Heptane/EtOH", keyReagents: "Silica" }
      ],
      yield: "5–15 % from seed biomass",
      purityTarget: "≥ 98 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Low-Moderate — limited by biomass supply; 100-g is realistic first target.",
      solventsUsed: ["Ethyl Acetate", "THF", "Methanol", "Ethanol", "Heptane"],
      equipment: ["Large extraction vessel", "Chromatography skid"],
      batchPlan: { targetBatch: "100 g 18-MC", startingMaterial: "~50 kg voacanga seeds", keyVolumes: ["EtOAc: 200 L", "MeOH: 50 L"] },
      hazards: ["Standard extraction; methylating agents toxic"],
      vendorNotes: ["Voacanga seeds: specialty herb suppliers (West African origin)"],
      estCostPerKg: "$80,000 – $140,000 at 100-g scale"
    },
    safety: {
      acuteToxicity: "Favorable in preclinical vs ibogaine",
      chronicConcerns: ["Long-term α3β4 blockade effects unknown"],
      contraindications: ["Pregnancy"],
      acuteSideEffects: ["Generally well-tolerated in rodents"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Glick SD, Maisonneuve IM. Development of novel medications for drug addiction: the legacy of an African shrub. Ann N Y Acad Sci. 2000;909:88-103." },
      { citation: "Savage DD et al. 18-Methoxycoronaridine, a non-toxic iboga alkaloid congener: effects on morphine and cocaine self-administration. J Pharmacol Exp Ther. 2006." }
    ],
    patentInfo: "Albany Molecular / Savant HWP / multiple licensees hold IP. Wohler can license research-scale or produce under academic-research exception.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 17, unmetMedicalNeed: 22, novelty: 12, universityInterest: 5 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 8, dependence: 2, schedulingRisk: 13 },
      netValue: 50,
      confidenceRationale: "MODERATE — proven mechanism, IP and biomass limitations"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Literature review on published synthesis", "Identify voacanga seed supply chain", "IP review"]
  }
};